import React, { useState, useEffect, useCallback } from 'react';
import { X, ShieldCheck } from 'lucide-react';

const STORAGE_KEY = 'ecomm-cookie-consent-v1';

const DEFAULT_PREFS = {
  necessary: true, // always on
  functional: false,
  analytics: false,
  advertising: false,
  consentedAt: null,
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persist = (prefs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / privacy errors */
  }
};

/**
 * Global cookie consent banner + preferences modal.
 * - Shown on first visit (no localStorage entry).
 * - Manage Cookies → opens preferences modal where user can toggle categories.
 * - Listens to global 'open-cookie-preferences' event so other links (e.g. footer
 *   "Manage Cookies" or Cookie Policy page) can re-open the preferences modal.
 */
const CookieConsent = () => {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  // On mount: show banner if user hasn't consented yet
  useEffect(() => {
    const existing = readStored();
    if (existing) {
      setPrefs(existing);
    } else {
      // Delay a beat so the page settles before the banner animates in
      const t = setTimeout(() => setBannerOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  // Listen for external "open preferences" events
  useEffect(() => {
    const handler = () => {
      setBannerOpen(false);
      setModalOpen(true);
    };
    window.addEventListener('open-cookie-preferences', handler);
    return () => window.removeEventListener('open-cookie-preferences', handler);
  }, []);

  const acceptAll = useCallback(() => {
    const next = {
      necessary: true,
      functional: true,
      analytics: true,
      advertising: true,
      consentedAt: new Date().toISOString(),
    };
    persist(next);
    setPrefs(next);
    setBannerOpen(false);
    setModalOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const next = {
      necessary: true,
      functional: false,
      analytics: false,
      advertising: false,
      consentedAt: new Date().toISOString(),
    };
    persist(next);
    setPrefs(next);
    setBannerOpen(false);
    setModalOpen(false);
  }, []);

  const savePrefs = () => {
    const next = { ...prefs, consentedAt: new Date().toISOString() };
    persist(next);
    setPrefs(next);
    setBannerOpen(false);
    setModalOpen(false);
  };

  const togglePref = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <>
      {/* Banner */}
      {bannerOpen && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-6 lg:left-auto lg:right-6 lg:max-w-xl z-[100] animate-[slideUpMobile_900ms_cubic-bezier(0.22,0.61,0.36,1)] sm:animate-[slideUp_720ms_ease-out]"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="relative bg-[#0a1929]/85 backdrop-blur-xl ring-1 ring-white/10 border border-white/10 shadow-2xl rounded-2xl text-white p-5 sm:p-6">
            <button
              type="button"
              onClick={() => setBannerOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
              aria-label="Close"
              data-testid="cookie-banner-close"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="text-[13px] sm:text-sm text-white/80 leading-relaxed text-justify pr-8 mb-5">
              We use cookies on this website to deliver the best site experience and analyse
              traffic. By clicking on <strong className="text-white">Allow All</strong>, you
              agree to our use of cookies. See our{' '}
              <a href="/cookies-policy" className="underline underline-offset-2 hover:text-white text-white/90">
                Cookie Policy
              </a>{' '}for more details. You can also manage your choices at anytime via{' '}
              <em className="not-italic text-white">Manage Cookies</em> in the footer.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5 sm:justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="text-sm font-medium text-white/85 hover:text-white border border-white/25 hover:border-white/60 rounded-lg px-5 py-2.5 transition-colors order-3 sm:order-1"
                data-testid="cookie-banner-manage-btn"
              >
                Manage Cookies
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="text-sm font-medium text-white/85 hover:text-white bg-white/10 hover:bg-white/15 rounded-lg px-5 py-2.5 transition-colors order-2"
                data-testid="cookie-banner-reject-btn"
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="text-sm font-semibold text-[#0a1929] bg-white hover:bg-white/90 rounded-lg px-5 py-2.5 transition-colors order-1 sm:order-3"
                data-testid="cookie-banner-consent-btn"
              >
                Allow All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          data-testid="cookie-preferences-modal"
        >
          {/* Scrim */}
          <button
            type="button"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setModalOpen(false)}
            aria-label="Close preferences"
          />
          {/* Modal */}
          <div className="relative bg-[#0a1929]/90 backdrop-blur-xl ring-1 ring-white/10 border border-white/10 shadow-2xl text-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col animate-[slideUp_360ms_ease-out]">
            <div className="flex items-start justify-between p-6 border-b border-white/10 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
                <p className="text-xs text-white/60 mt-0.5">
                  Choose which categories of cookies you allow
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                aria-label="Close"
                data-testid="cookie-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-3">
              <CategoryRow
                title="Strictly Necessary"
                description="Essential to enable you to move around our website and use its features such as accessing secure areas. Without these cookies, services you have asked for cannot be provided."
                checked
                disabled
                badge="Always active"
                testId="cookie-pref-necessary"
              />
              <CategoryRow
                title="Functional"
                description="Allow our website to remember choices you make and to provide enhanced, more personal features such as sharing content on social media and other third-party features."
                checked={prefs.functional}
                onChange={() => togglePref('functional')}
                testId="cookie-pref-functional"
              />
              <CategoryRow
                title="Analytics"
                description="Help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve how our site works."
                checked={prefs.analytics}
                onChange={() => togglePref('analytics')}
                testId="cookie-pref-analytics"
              />
              <CategoryRow
                title="Advertising"
                description="Used to monitor the effectiveness of our online advertising campaigns. No individual users can be identified by the data these third-party cookies collect."
                checked={prefs.advertising}
                onChange={() => togglePref('advertising')}
                testId="cookie-pref-advertising"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5 p-6 border-t border-white/10 sm:justify-end flex-shrink-0">
              <button
                type="button"
                onClick={rejectAll}
                className="text-sm font-medium text-white/85 hover:text-white bg-white/10 hover:bg-white/15 rounded-lg px-5 py-2.5 transition-colors"
                data-testid="cookie-modal-reject-all"
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="text-sm font-medium text-white/85 hover:text-white border border-white/25 hover:border-white/60 rounded-lg px-5 py-2.5 transition-colors"
                data-testid="cookie-modal-save"
              >
                Save My Preferences
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="text-sm font-semibold text-[#0a1929] bg-white hover:bg-white/90 rounded-lg px-5 py-2.5 transition-colors"
                data-testid="cookie-modal-accept-all"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CategoryRow = ({ title, description, checked, onChange, disabled, badge, testId }) => (
  <div className="flex items-start justify-between gap-4 p-4 border border-white/10 bg-white/[0.03] rounded-xl hover:bg-white/[0.06] transition-colors">
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h4 className="text-sm font-semibold text-white">{title}</h4>
        {badge && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white/80 bg-white/10 px-2 py-0.5 rounded-full">
            <ShieldCheck className="w-3 h-3" />
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs sm:text-sm text-white/65 leading-relaxed">{description}</p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
      />
      <div
        className={`w-11 h-6 rounded-full transition-colors ${
          checked ? 'bg-white' : 'bg-white/20'
        } ${disabled ? 'opacity-60' : ''} peer-focus:ring-2 peer-focus:ring-white/40`}
      >
        <div
          className={`w-5 h-5 ${checked ? 'bg-[#0a1929]' : 'bg-white'} rounded-full shadow transform transition-transform mt-0.5 ${
            checked ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`}
        />
      </div>
    </label>
  </div>
);

export default CookieConsent;
